import { useLazyQuery, useMutation } from "@apollo/client";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Download from "@mui/icons-material/Download";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNew from "@mui/icons-material/OpenInNew";
import PersonAdd from "@mui/icons-material/PersonAdd";
import PersonRemove from "@mui/icons-material/PersonRemove";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import CopyableTypography from "components/Typography/CopyableTypography";
import {
  VerifyAndSubmitFormDialog,
  VerifyAndSubmitFormState,
} from "components/VerifyAndSubmitFormDialog";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import {
  TierIiReportEncampStatus,
  TierIiReportKind,
  TierIiReportOrgStatus,
} from "generated-graphql/graphql";
import {
  AssignDialog,
  useUnassignActivities,
} from "hooks/activities/assignment";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { compact } from "lodash-es";
import { BULK_VERIFY_MUTATION } from "queries/tierIIVerification";
import { useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { useFulfillmentDetails } from "./useFulfillmentDetails";

const UPDATE_NEEDS_REVISION_MUTATION = gql(`
  mutation UpdateTierIIReportNeedsRevision($id: ID!, $needsRevision: Boolean!, $needsRevisionReason: String) {
    updateTierIIReportNeedsRevision(id: $id, needsRevision: $needsRevision, needsRevisionReason: $needsRevisionReason) {
      id
      needsRevision
      needsRevisionReason
    }
  }
`);

const GET_SNAPSHOT_CONTENT_QUERY = gql(`
  query GetTierIIReportSnapshotContent($reportId: ID!) {
    tierIIReportSnapshotContent(reportId: $reportId)
  }
`);

// Custom FieldDisplay for reporting with wider label alignment
const ReportingFieldContainer = styled("p")(({ theme }) => ({
  margin: 0,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  fontSize: "16px",
}));

const ReportingFieldLabel = styled("span")(({ theme }) => ({
  fontWeight: 500,
  fontSize: "12px",
  opacity: 0.6,
  minWidth: "160px", // Wider than default 120px for better alignment
  display: "inline-block",
}));

const ReportingFieldDisplay = ({
  label,
  value,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
}) => (
  <ReportingFieldContainer>
    <ReportingFieldLabel>{label}</ReportingFieldLabel>
    <span>{value}</span>
  </ReportingFieldContainer>
);

export function Reporting() {
  const theme = useTheme();
  const { data, reportUrl, inventoryUrl } = useFulfillmentDetails();
  const alerts = useAlerts();
  const [mutate, { loading: bulkVerifyLoading }] =
    useMutation(BULK_VERIFY_MUTATION);

  const [dialogState, setDialogState] = useState<
    null | "verifyAndSubmit" | "assign"
  >(null);

  const [isExpanded, setIsExpanded] = useLocalStorageState<boolean>(
    "fulfillment-reporting-expanded",
    { defaultValue: true }
  );

  const { unassign, loading: unassigning } = useUnassignActivities();

  const [fetchSnapshotContent, { loading: snapshotLoading }] = useLazyQuery(
    GET_SNAPSHOT_CONTENT_QUERY,
    {
      fetchPolicy: "network-only",
      onCompleted: (result) => {
        if (result.tierIIReportSnapshotContent) {
          const content = JSON.stringify(
            result.tierIIReportSnapshotContent,
            null,
            2
          );
          const blob = new Blob([content], { type: "application/json" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          const facilityName =
            data?.tierIIReport?.facility?.name?.replace(/[^a-zA-Z0-9]/g, "-") ??
            "facility";
          const reportId = data?.tierIIReport?.id ?? "unknown";
          link.download = `snapshot-${facilityName}-${reportId}.json`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } else {
          alerts.error("No snapshot found for this report");
        }
      },
      onError: (error) => {
        alerts.error("Failed to download snapshot", error);
      },
    }
  );

  const handleDownloadSnapshot = () => {
    if (data?.tierIIReport?.id) {
      fetchSnapshotContent({ variables: { reportId: data.tierIIReport.id } });
    }
  };

  // Needs revision state - initialized from server data
  const [needsRevision, setNeedsRevision] = useState(false);
  const [needsRevisionReason, setNeedsRevisionReason] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSavePending, setIsSavePending] = useState(false);

  const [updateNeedsRevision] = useMutation(
    UPDATE_NEEDS_REVISION_MUTATION,
    {
      onError: (error) => {
        setIsSavePending(false);
        alerts.error("Failed to update revision status", error);
      },
      onCompleted: () => {
        setIsSavePending(false);
        alerts.success("Needs revision status updated");
      },
    }
  );

  // Sync local state with server data on initial load
  const tierIIReport = data?.tierIIReport;
  useEffect(() => {
    if (tierIIReport && !isInitialized) {
      setNeedsRevision(tierIIReport.needsRevision ?? false);
      setNeedsRevisionReason(tierIIReport.needsRevisionReason ?? "");
      setIsInitialized(true);
    }
  }, [tierIIReport, isInitialized]);

  // Debounced save function
  const debouncedSave = useDebouncedCallback(
    (checked: boolean, reason: string) => {
      if (!data?.tierIIReport?.id) return;

      updateNeedsRevision({
        variables: {
          id: data.tierIIReport.id,
          needsRevision: checked,
          needsRevisionReason: checked ? reason : null,
        },
      });
    },
    500
  );

  const handleNeedsRevisionChange = (checked: boolean) => {
    setNeedsRevision(checked);
    setIsSavePending(true);
    debouncedSave(checked, needsRevisionReason);
  };

  const handleReasonChange = (reason: string) => {
    setNeedsRevisionReason(reason);
    setIsSavePending(true);
    debouncedSave(needsRevision, reason);
  };

  const closeDialogs = () => setDialogState(null);

  const onSubmit = async (formState: VerifyAndSubmitFormState) => {
    if (data?.tierIIReport?.id) {
      mutate({
        variables: {
          reportIds: [data.tierIIReport.id],
          verifier: {
            verifiedAt: formState.date,
            verifierFullName: `${formState.verifierFullName}`,
            verifierTitle: formState.verifierTitle,
            verifierEmail: formState.verifierEmail,
            verifierPhone: formState.verifierPhone,
          },
        },
        refetchQueries: ["GetTierIIReport"],
        update: (cache, { data: mutationResponse }) => {
          if (mutationResponse?.bulkVerifyAndSubmitReports?.succeeded) {
            cache.modify({
              id: cache.identify({
                __typename: "TierIIReport",
                id: data.tierIIReport.id,
              }),
              fields: {
                encampStatus: () => TierIiReportEncampStatus.Processing,
                organizationStatus: () => TierIiReportOrgStatus.Verified,
              },
            });
          }
        },
        onCompleted: () => {
          alerts.success("Report verified and submitted");
        },
        onError: (err) => {
          alerts.error("Failed to verify and submit report", err);
        },
      });
    }
  };

  const hasOpenReview =
    data?.tierIIReport.reviewers?.some((r) => !r.isComplete) ?? false;
  const hasValidationErrors = (data?.tierIIReport.issues ?? []).length > 0;

  const isVerified =
    data?.tierIIReport?.organizationStatus === TierIiReportOrgStatus.Verified;

  const headerButtons = (
    <Stack direction="row" spacing={1} alignItems="center">
      <Button
        component="a"
        href={reportUrl}
        target="_blank"
        rel="noopener noreferrer"
        size="small"
        disabled={!reportUrl}
        startIcon={<OpenInNew sx={{ fontSize: 18 }} />}
      >
        Open Report
      </Button>
      <Button
        component="a"
        href={inventoryUrl}
        target="_blank"
        rel="noopener noreferrer"
        size="small"
        disabled={!inventoryUrl}
        startIcon={<OpenInNew sx={{ fontSize: 18 }} />}
      >
        Open Inventory
      </Button>
      <Button
        size="small"
        disabled={!isVerified || snapshotLoading}
        onClick={handleDownloadSnapshot}
        startIcon={
          snapshotLoading ? (
            <CircularProgress size={16} />
          ) : (
            <Download sx={{ fontSize: 18 }} />
          )
        }
      >
        Snapshot
      </Button>
    </Stack>
  );

  return (
    <Stack
      direction="column"
      component={Paper}
      elevation={0}
      spacing={2}
      padding={theme.spacing(2, 3, 3, 3)}
    >
      {/* Header with title and action button */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ flexWrap: "wrap", gap: 1 }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Typography variant="h6">Reporting</Typography>
          <IconButton size="small" sx={{ ml: 1 }}>
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        {headerButtons}
      </Stack>

      {/* Always visible fields */}
      <Stack direction="column" spacing={1}>
        <ReportingFieldDisplay
          label="Organization Status"
          value={
            <CopyableTypography
              text={
                prettyPrintEnumValue(data?.tierIIReport.organizationStatus) ??
                "N/A"
              }
            />
          }
        />
        <ReportingFieldDisplay
          label="Encamp Status"
          value={
            <CopyableTypography
              text={
                prettyPrintEnumValue(data?.tierIIReport.encampStatus) ?? "N/A"
              }
            />
          }
        />
        <ReportingFieldDisplay
          label="Number of chemicals"
          value={data?.tierIIReport.productCount?.toString() ?? "N/A"}
        />
        <ReportingFieldDisplay
          label="Current Activity Assignee"
          value={
            <CopyableTypography
              text={
                data?.tierIIReport.currentWorkflow?.currentActivity?.assignee
                  ?.email ?? "N/A"
              }
            />
          }
        />
      </Stack>

      {/* Additional fields with animation */}
      <Collapse in={isExpanded}>
        <Stack direction="column" spacing={1}>
          <ReportingFieldDisplay
            label="Report Kind"
            value={
              <CopyableTypography
                text={
                  prettyPrintEnumValue(data?.tierIIReport.reportKind) ?? "N/A"
                }
              />
            }
          />
          {data?.tierIIReport.reportKind === TierIiReportKind.Revision && (
            <ReportingFieldDisplay
              label="Reason for Revision"
              value={
                <CopyableTypography
                  text={data?.tierIIReport.reasonForReport ?? "N/A"}
                />
              }
            />
          )}
          <ReportingFieldDisplay
            label="Portal Submission Mode"
            value={
              data?.tierIIReport.facility.reportsManually ? "Manual" : "Auto"
            }
          />
          <ReportingFieldDisplay
            label={
              <Tooltip title="Portal Interaction Record Status">
                <span>PIR Status</span>
              </Tooltip>
            }
            value={
              <CopyableTypography
                text={
                  prettyPrintEnumValue(
                    data?.tierIIReport.facility.jobs?.[0]?.status
                  ) || "N/A"
                }
              />
            }
          />

          {/* Spacer for button spacing */}
          <Box sx={{ height: theme.spacing(2) }} />

          {/* Action buttons for report - only visible when expanded */}
          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexWrap: "wrap",
              gap: theme.spacing(1),
            }}
          >
            <Button
              disabled={
                bulkVerifyLoading ||
                data?.tierIIReport?.encampStatus !==
                  TierIiReportEncampStatus.AwaitingVerification
              }
              startIcon={
                bulkVerifyLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <CheckCircle sx={{ fontSize: 18 }} />
                )
              }
              onClick={() => setDialogState("verifyAndSubmit")}
              size="small"
            >
              Verify and Submit
            </Button>
            <Button
              disabled={
                !data?.tierIIReport.currentWorkflow?.currentActivity?.id
              }
              startIcon={<PersonAdd sx={{ fontSize: 18 }} />}
              onClick={() => setDialogState("assign")}
              size="small"
            >
              Assign Activity
            </Button>
            <Button
              disabled={
                unassigning ||
                !data?.tierIIReport.currentWorkflow?.currentActivity?.assignee
              }
              startIcon={
                unassigning ? (
                  <CircularProgress size={16} />
                ) : (
                  <PersonRemove sx={{ fontSize: 18 }} />
                )
              }
              onClick={() =>
                unassign(
                  compact([
                    data?.tierIIReport.currentWorkflow?.currentActivityId,
                  ])
                )
              }
              size="small"
            >
              Unassign Activity
            </Button>
          </Stack>

          {/* Needs Revision section - only show for ANNUAL reports */}
          {data?.tierIIReport.reportKind === TierIiReportKind.Annual && (
            <>
              {/* Spacer before needs revision */}
              <Box />

              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={needsRevision}
                      onChange={(e) =>
                        handleNeedsRevisionChange(e.target.checked)
                      }
                      disabled={!data?.tierIIReport?.id}
                      size="small"
                    />
                  }
                  label={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <span>Revision needed</span>
                      {isSavePending && <CircularProgress size={14} />}
                    </Stack>
                  }
                  sx={{ ml: 0 }}
                />
                <Collapse in={needsRevision}>
                  <TextField
                    value={needsRevisionReason}
                    onChange={(e) => handleReasonChange(e.target.value)}
                    label="Explanation for Revision"
                    disabled={!data?.tierIIReport?.id}
                    multiline
                    rows={2}
                    size="small"
                    fullWidth
                    sx={{ mt: 1 }}
                  />
                </Collapse>
              </Box>
            </>
          )}
        </Stack>
      </Collapse>

      {/* Dialogs */}
      <VerifyAndSubmitFormDialog
        open={dialogState === "verifyAndSubmit"}
        onClose={closeDialogs}
        onSubmit={onSubmit}
        loading={bulkVerifyLoading}
        defaultVerifier={{
          fullName: data?.tierIIReport?.verifierFullName ?? undefined,
          title: data?.tierIIReport?.verifierTitle ?? undefined,
          person: undefined,
        }}
        withConfirmation={hasOpenReview || hasValidationErrors}
        skippedDueToOpenReviewsCount={hasOpenReview ? 1 : 0}
        skippedDueToVerificationCount={0}
        verifiableRowsWithoutOpenReviewsCount={hasOpenReview ? 0 : 1}
        reportsWithValidationErrorsCount={hasValidationErrors ? 1 : 0}
        isReportKindAnnual={
          data?.tierIIReport?.reportKind === TierIiReportKind.Annual
        }
        reportIds={data?.tierIIReport?.id ? [data.tierIIReport.id] : []}
      />
      <AssignDialog
        open={dialogState === "assign"}
        onClose={closeDialogs}
        activityIds={compact([
          data?.tierIIReport.currentWorkflow?.currentActivity?.id,
        ])}
      />
    </Stack>
  );
}
